import { EGender } from './chat';
import { EModerationDecision, INpcAvatarInfo } from './feed';
import { EReferenceType, IHeadPosition } from './npcBase';

export enum EMaterialType {
  MaterialType_Avatar = 1,
  MaterialType_Voice = 2,
  MaterialType_Profile = 3,
}

export interface IGetMaterialListReqFilter {
  user_id: number;
  material_type: EMaterialType; // 素材类型
}

export interface IGetMaterialListReq {
  page: number;
  size: number;
  get_material_list_req_filter: IGetMaterialListReqFilter;
}

export interface IAuthor {
  user_id?: bigint;
  user_name?: string;
  user_avatar_url?: string;
}

export interface IMaterialAvatarData {
  material_id?: number; // 素材id，唯一标识
  material_name?: string; // 素材名称
  avatar_info?: INpcAvatarInfo; // 形象库数据
  moderation_decision?: EModerationDecision; // 审核状态
  author_id?: number; // 素材的创建者
  owner_id?: number; // 素材的拥有者
  user_define_gender?: EGender; // 用户定义的性别
  using_count?: number; // 使用次数
  parent_public_avatar_data?: IMaterialAvatarData; // 素材模版垫图数据，公域概念。
  parent_private_avatar_data?: IMaterialAvatarData; // 素材垫图数据，私域概念。
  author?: IAuthor; // 素材作者
  is_published_as_post?: boolean; // 是否作为动态发布过
  web_preview_extra?: IWebPreviewExtra;
}

export interface IMaterialVoiceData {
  material_id: number; // 素材id，唯一标识
  material_name: string; // 素材名称
  npc_tone: Record<string, number>; // 音色
  speed: number; // 语速
  pitch: number; // 音调
  prologue: string; // 开场白
  prologue_wav: string; // 开场白语音链接（服务端异步生成)
  prologue_wav_duration: number; // 开场白语音时长(（服务端异步生成)
  moderation_decision: EModerationDecision; // 审核状态
  author_id: number; // 素材的创建者
  owner_id: number; // 素材的拥有者
  user_define_gender: EGender; // 用户定义的性别
}

export interface IGetMaterialListResp {
  material_avatar_data_list?: IMaterialAvatarData[];
  material_voice_data_list?: IMaterialVoiceData[];

  has_more: boolean;
  total_cnt: number;
}

export interface ICreateMaterialReq {
  material_type?: EMaterialType; // 素材类型
  material_avatar_data?: IMaterialAvatarData; // material_type = avatar时，传该字段
  material_voice_data?: IMaterialVoiceData; // material_type = voice时，传该字段
  original_avatar_material_id?: number; // 垫图时的原始素材id
}

export interface ICreateMaterialResp {
  material_id?: number; // 素材id，唯一标识
  material_name?: string; // 素材名
}

export interface IUpdateMaterialReq {
  material_id?: number; // 素材id，唯一标识
  material_name?: string; // 素材名
  material_voice_data?: IMaterialVoiceData; // 更新声音素材。 如果字段有值，以该字段为准。
}

export interface IDeleteMaterialReq {
  material_id?: number; // 素材id，唯一标识
}

enum VoiceType {
  UserCreate = 1, // ai 生成
  Official = 2,
  CustomCreate = 3, // 用户自定义创建
  TextCreate = 4, // 文本生成
}

export interface IVoiceSelection {
  voice_id: string;
  voice_name: string;
  voice_tag: string[];
  voice_demo_url: string;
  voice_type: VoiceType;
  hidden_prompt: string;
  gender: string;
  age: string;
  material_id: number;
}

export interface IGetVoiceRepoWithTagReq {
  user_id: number;
  npc_tone?: Record<string, number>;
  tag_gender?: string;
  user_define_gender?: EGender;
  tag_age?: string;
}

export interface IGetVoiceRepoWithTagResp {
  voice_list: IVoiceSelection[];
  user_voice: IVoiceSelection[];
  valid_gender_list: string[];
  valid_age_list: string[];
  user_define_gender_str: string;
}

export interface IRefinePromptReq {
  img_url?: string;
  user_id?: number;
  prompt?: string;
  position?: IHeadPosition;
  user_define_gender?: EGender; // 用户输入的性别
}

export interface IRefinePromptResp {
  refine_prompt: string;
}

export enum EPreviewAvatarScene {
  Default = 0,
  Material = 1,
  Aiclone = 2,
  TemplatePreview = 3,
  WebPreview = 4, // web 生成预览图
  WebPreviewRegen = 5, // web 预览图重绘
}

export enum EImageModelType {
  Common = 1, // 通用模型
  Anime = 2, // 动漫模型
  Realistic = 3, // 真人模型
}

export interface IWebPreviewExtra {
  style_weight?: number; // 画风权重
  face_weight?: number; // 脸权重
  cfg_scale?: number; // 缩放比例
  img_model_id?: string; // 图像模型id
  style_img_url?: string; // sytle的垫图
  face_img_url?: string; // face的垫图
}

export interface IPreviewAvatarReq {
  base_img_url?: string;
  prompt?: string;
  position?: IHeadPosition;
  user_define_gender?: EGender; // 用户输入的性别
  reference?: EReferenceType[];
  avatar_style_id_list?: number[]; // 画风id列表
  preview_avatar_scene?: EPreviewAvatarScene; // 预览图场景
  is_dsync?: boolean; // 是否为异步
  web_preview_extra?: IWebPreviewExtra;
}

export interface IImgElem {
  img_url?: string;
  depth_img_url?: string;
  keywords?: string[];
  style_name?: string;
  extra?: string;
  avatar_style_id?: number; // 这张图用了哪个画风id
  biz_extra?: string; // 业务方自定义的额外信息
  img_id?: number; // 生成的图片id
  wait_copywriting?: string; // 等待的文案
  wait_time?: number; // 等待时间，单位s
  web_preview_extra?: IWebPreviewExtra;
}

export interface IPreviewAvatarResp {
  img_list?: IImgElem[];
}

export interface IPollPreviewAvatarReq {
  img_id_list: number[]; // 图片id列表
}

export interface IPollPreviewAvatarResp {
  img_list?: IImgElem[];
}

export interface IGetMaterialByIdReq {
  material_id_list?: number[]; // 素材id，唯一标识
}

export interface IMaterialInfo {
  material_type?: EMaterialType; // 素材类型
  material_avatar_data?: IMaterialAvatarData; // material_type = MaterialType_Avatar时，传该字段
  material_voice_data?: IMaterialVoiceData; // material_type = MaterialType_Voice时，传该字段
  deleted_at_milli?: number; // 删除时间，毫秒
}

export interface IGetMaterialByIdResp {
  material_infos: {
    [key: number]: IMaterialInfo;
  };
}

export interface IQueryWebInpaintTimesReq {}

export interface IQueryWebInpaintTimesResp {
  remain_inpaint_times?: number; // 剩余次数
  today_inpaint_limit_times?: number; // 每日上限次数
}

export enum EPageQueryAvatarStyleScene {
  WebList = 1, // web端画风列表
  ListAll = 2, // 所有画风列表
}

export interface IPageQueryAvatarStyleReq {
  offset?: number; // 从0开始
  limit?: number; // 返回个数
  user_define_gender?: EGender; // Assuming Gender is imported or defined elsewhere
  insert_style_name?: string; // 强插到第一个的style名称 => 废弃
  category?: string; // 画风分类 填空默认全部 => 废弃
  insert_style_id?: number;
  category_id?: number;
  model_id?: string; // 模型id
  scene?: EPageQueryAvatarStyleScene; // 场景
}

// 画风类别
export interface IStyleCategory {
  category_id?: number;
  category_name?: string;
}

export interface AvatarStyleElem {
  avatar_style_id?: number;
  avatar_style_name?: string;
  avatar_style_img_url?: string;
  copywriting?: string;
  copywriting_color?: { [key: string]: number[] };
  style_show_name?: string; // 前端展示的名字
}

export interface IPageQueryAvatarStyleResp {
  avatar_style_list?: AvatarStyleElem[];
  has_more?: boolean;
  category_list?: IStyleCategory[];
}

export enum EModerationImgScene {
  Default = 0,
  AvatarFromPhotoAlbum = 1, // 从相册上传的图片
  FMBgImg = 2, // fm 背景图
  FMBgImgJSB = 3, // fm 背景图-客户端jsb方式
}

export interface IModerationImgResp {
  img_safe_list?: boolean[];
}

export enum EPrivacySetting {
  Public = 1,
  Private = 2,
}

export interface IModerationImgReq {
  img_url?: string[];
  privacy_setting?: EPrivacySetting; // 私密 or 公开的资源
  scene?: EModerationImgScene; // 区分图片校验的场景
}

export interface IQueryPictureStyleDetailReq {
  id?: number; // style_id
  style_name?: string; // 画风名称
}

export interface IQueryPictureStyleDetailResp {
  style?: INpcPictureStyle;
}

export interface INpcPictureStyle {
  id?: number; // 画风id style_id
  style_name?: string; // 画风名称
  style_id?: string; // 画风id，对应的是styleID，计算平台使用的id
  style_show_name?: string; // 画风展示名称
  pic_url?: string;
  model_name?: string; // 模型名称
}

export interface IGetWebPaintStyleReq {
  model_id?: string; // 画风id
}

export interface IAvatarStyleList {
  [key: number]: AvatarStyleElem[];
}

export interface IGetWebPaintStyleResp {
  avatar_style_list?: IAvatarStyleList;
  category_list?: IStyleCategory[];
}

export interface ICreateUserToneModelReq {
  wav_url?: string[];
  voice_type?: VoiceType;
  voice_name?: string;
  file_list?: IDuplicateVoiceFile[];
}
export interface IDuplicateVoiceFile {
  file_url: string;
  file_extension?: string; //.后面的文件后缀名，支持"mp4", "avi", "mov", "wmv", "mkv", "flv", "mpeg", "mpg", "webm", "3pg"，"mp3", "wav", "aac", "flac", "ogg", "m4a", "wma", "alac", "aiff"
}
export interface ICreateUserToneModelResp {
  voice_id?: string;
  category?: string;
  model?: string;
  source?: string;
  voice_demo_url?: string;
  voice_name?: string;
  material_id?: number;
}

export interface IDeleteMaterialResp {}
